import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Store, Plus, Edit, Trash2, MapPin, Package, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Turkish cities list
const TURKISH_CITIES = [
  "Adana", "Adıyaman", "Afyonkarahisar", "Ağrı", "Aksaray", "Amasya", "Ankara", "Antalya", "Ardahan", "Artvin",
  "Aydın", "Balıkesir", "Bartın", "Batman", "Bayburt", "Bilecik", "Bingöl", "Bitlis", "Bolu", "Burdur",
  "Bursa", "Çanakkale", "Çankırı", "Çorum", "Denizli", "Diyarbakır", "Düzce", "Edirne", "Elazığ", "Erzincan",
  "Erzurum", "Eskişehir", "Gaziantep", "Giresun", "Gümüşhane", "Hakkari", "Hatay", "Iğdır", "Isparta", "İstanbul",
  "İzmir", "Kahramanmaraş", "Karabük", "Karaman", "Kars", "Kastamonu", "Kayseri", "Kırıkkale", "Kırklareli", "Kırşehir",
  "Kilis", "Kocaeli", "Konya", "Kütahya", "Malatya", "Manisa", "Mardin", "Mersin", "Muğla", "Muş",
  "Nevşehir", "Niğde", "Ordu", "Osmaniye", "Rize", "Sakarya", "Samsun", "Şanlıurfa", "Siirt", "Sinop",
  "Şırnak", "Sivas", "Tekirdağ", "Tokat", "Trabzon", "Tunceli", "Uşak", "Van", "Yalova", "Yozgat", "Zonguldak"
];

// Predefined colors for dealers
const DEALER_COLORS = [
  { name: "Mavi", value: "#3B82F6" },
  { name: "Yeşil", value: "#22C55E" },
  { name: "Mor", value: "#8B5CF6" },
  { name: "Turuncu", value: "#F97316" },
  { name: "Pembe", value: "#EC4899" },
  { name: "Cyan", value: "#06B6D4" },
  { name: "Kırmızı", value: "#EF4444" },
  { name: "Sarı", value: "#EAB308" },
  { name: "Indigo", value: "#6366F1" },
  { name: "Teal", value: "#14B8A6" },
];

const Dealers = () => {
  const [dealers, setDealers] = useState([]);
  const [dealersStats, setDealersStats] = useState([]);
  const [assignedCities, setAssignedCities] = useState(new Set());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [citySearch, setCitySearch] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    contact_person: "",
    phone: "",
    email: "",
    assigned_cities: [],
    color: "#3B82F6",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dealersRes, statsRes] = await Promise.all([
        axios.get(`${API}/dealers`),
        axios.get(`${API}/dealers-stats`),
      ]);
      setDealers(dealersRes.data);
      setDealersStats(statsRes.data);
      
      // Build set of already assigned cities
      const assigned = new Set();
      dealersRes.data.forEach(dealer => {
        dealer.assigned_cities?.forEach(city => assigned.add(city));
      });
      setAssignedCities(assigned);
    } catch (error) {
      console.error("Error fetching dealers:", error);
      toast.error("Bayiler yüklenirken hata oluştu");
    }
  };

  const handleOpenDialog = (dealer = null) => {
    if (dealer) {
      setIsEditMode(true);
      setSelectedDealer(dealer);
      setFormData({
        name: dealer.name,
        contact_person: dealer.contact_person,
        phone: dealer.phone,
        email: dealer.email,
        assigned_cities: dealer.assigned_cities || [],
        color: dealer.color || "#3B82F6",
      });
    } else {
      setIsEditMode(false);
      setSelectedDealer(null);
      setFormData({
        name: "",
        contact_person: "",
        phone: "",
        email: "",
        assigned_cities: [],
        color: "#3B82F6",
      });
    }
    setCitySearch("");
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEditMode && selectedDealer) {
        await axios.put(`${API}/dealers/${selectedDealer.id}`, formData);
        toast.success("Bayi güncellendi");
      } else {
        await axios.post(`${API}/dealers`, formData);
        toast.success("Bayi oluşturuldu");
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving dealer:", error);
      const message = error.response?.data?.detail || "İşlem başarısız";
      toast.error(message);
    }
  };

  const handleDelete = async (dealer) => {
    if (!window.confirm(`${dealer.name} bayisini silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/dealers/${dealer.id}`);
      toast.success("Bayi silindi");
      fetchData();
    } catch (error) {
      console.error("Error deleting dealer:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleCityToggle = (city) => {
    setFormData((prev) => {
      const cities = prev.assigned_cities.includes(city)
        ? prev.assigned_cities.filter((c) => c !== city)
        : [...prev.assigned_cities, city];
      return { ...prev, assigned_cities: cities };
    });
  };

  const getDealerStats = (dealerId) => {
    return dealersStats.find(s => s.dealer_id === dealerId) || {
      total_quantity: 0,
      total_amount: 0,
      total_deliveries: 0
    };
  };

  const filteredCities = TURKISH_CITIES.filter(city => 
    city.toLowerCase().includes(citySearch.toLowerCase())
  );

  const isCityAssignedToOther = (city) => {
    if (isEditMode && selectedDealer) {
      // In edit mode, don't mark cities assigned to current dealer
      return assignedCities.has(city) && !selectedDealer.assigned_cities?.includes(city);
    }
    return assignedCities.has(city);
  };

  // Calculate totals
  const totalQuantity = dealersStats.reduce((sum, s) => sum + s.total_quantity, 0);
  const totalAmount = dealersStats.reduce((sum, s) => sum + s.total_amount, 0);

  return (
    <div className="space-y-6" data-testid="dealers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="dealers-title">
            Bayiler
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            Bayi yönetimi ve bölge atamaları
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-dealer-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Bayi Ekle
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Store className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam Bayi</p>
                <p className="text-2xl font-bold">{dealers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <MapPin className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Atanan Şehir</p>
                <p className="text-2xl font-bold">{assignedCities.size}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam Satış</p>
                <p className="text-2xl font-bold">{totalQuantity} adet</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam Ciro</p>
                <p className="text-2xl font-bold">{totalAmount.toLocaleString('tr-TR')} ₺</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dealers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dealers.map((dealer) => {
          const stats = getDealerStats(dealer.id);
          return (
            <Card key={dealer.id} className="overflow-hidden" data-testid={`dealer-card-${dealer.id}`}>
              <div className="h-2" style={{ backgroundColor: dealer.color || "#3B82F6" }} />
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: dealer.color || "#3B82F6" }}
                    >
                      {dealer.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{dealer.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{dealer.contact_person}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(dealer)}
                      data-testid={`edit-dealer-${dealer.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(dealer)}
                      data-testid={`delete-dealer-${dealer.id}`}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Telefon</p>
                    <p className="font-medium">{dealer.phone}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">E-posta</p>
                    <p className="font-medium truncate" title={dealer.email}>{dealer.email}</p>
                  </div>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 p-3 bg-secondary rounded-lg">
                  <div className="text-center">
                    <p className="text-lg font-bold text-purple-600">{stats.total_quantity}</p>
                    <p className="text-xs text-muted-foreground">Adet</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{stats.total_deliveries}</p>
                    <p className="text-xs text-muted-foreground">Teslimat</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-orange-600">{(stats.total_amount / 1000).toFixed(1)}K</p>
                    <p className="text-xs text-muted-foreground">Ciro (₺)</p>
                  </div>
                </div>

                {/* Assigned Cities */}
                <div>
                  <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Bölgeler ({dealer.assigned_cities?.length || 0})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {dealer.assigned_cities?.slice(0, 5).map((city) => (
                      <span
                        key={city}
                        className="text-xs px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: dealer.color || "#3B82F6" }}
                      >
                        {city}
                      </span>
                    ))}
                    {dealer.assigned_cities?.length > 5 && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                        +{dealer.assigned_cities.length - 5} daha
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {dealers.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Store className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Henüz bayi bulunmamaktadır</p>
              <Button className="mt-4" onClick={() => handleOpenDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                İlk Bayiyi Ekle
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dealer Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dealer-dialog">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? "Bayiyi Düzenle" : "Yeni Bayi Ekle"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Bayi Adı *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Örn: ABC Medikal"
                  data-testid="dealer-name-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="contact_person">Yetkili Kişi *</Label>
                <Input
                  id="contact_person"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  placeholder="Ad Soyad"
                  data-testid="dealer-contact-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Telefon *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="0532 xxx xx xx"
                  data-testid="dealer-phone-input"
                  required
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="email">E-posta *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ornek@email.com"
                  data-testid="dealer-email-input"
                  required
                />
              </div>

              <div className="col-span-2">
                <Label>Bayi Rengi</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {DEALER_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${
                        formData.color === color.value ? "border-gray-900 scale-110" : "border-transparent"
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                      data-testid={`color-${color.value}`}
                    />
                  ))}
                </div>
              </div>

              <div className="col-span-2">
                <Label>Sorumlu Bölgeler (Şehirler)</Label>
                <Input
                  placeholder="Şehir ara..."
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  className="mt-2 mb-3"
                  data-testid="city-search-input"
                />
                <div className="max-h-48 overflow-y-auto border rounded-lg p-3 grid grid-cols-3 gap-2">
                  {filteredCities.map((city) => {
                    const isAssignedToOther = isCityAssignedToOther(city);
                    return (
                      <div key={city} className="flex items-center space-x-2">
                        <Checkbox
                          id={`city-${city}`}
                          checked={formData.assigned_cities.includes(city)}
                          onCheckedChange={() => handleCityToggle(city)}
                          disabled={isAssignedToOther}
                          data-testid={`city-checkbox-${city}`}
                        />
                        <Label 
                          htmlFor={`city-${city}`} 
                          className={`text-sm cursor-pointer ${isAssignedToOther ? 'text-muted-foreground line-through' : ''}`}
                        >
                          {city}
                          {isAssignedToOther && " (atanmış)"}
                        </Label>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Seçili: {formData.assigned_cities.length} şehir
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setIsDialogOpen(false)}
                data-testid="cancel-dealer-btn"
              >
                İptal
              </Button>
              <Button
                type="submit"
                className="flex-1"
                data-testid="save-dealer-btn"
              >
                {isEditMode ? "Güncelle" : "Oluştur"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dealers;
